import { get, post, authFetch } from '../request';

// ─── Types ──────────────────────────────────────────────

export interface JobItem {
  jobId: string;
  status: 'waiting' | 'active' | 'completed' | 'failed' | 'queued';
  sessionId: string | null;
  description: string | null;
  count: number | null;
  progress: number;
  completed: number;
  createdAt: string;
  finishedAt: string | null;
  failedReason: string | null;
  generationMethod: 'manual' | 'variant' | null;
  generationMode: string | null;
  sourcePlayableId: string | null;
}

export interface BatchJobReturnValue {
  jobId: string;
  sessionIds: string[];
  completed: number;
  failed: number;
  variants?: Array<{ name?: string; quality?: { score?: number }; size?: number; previewUrl?: string }>;
}

export interface BatchJobStatus {
  jobId: string;
  status: string;
  progress: number;
  total: number;
  completed: number;
  sessionId: string | null;
  createdAt: string;
  finishedAt: string | null;
  failedReason: string | null;
  returnValue: BatchJobReturnValue | null;
  /** 后端直接返回的变体列表（优先于 returnValue.variants） */
  variants?: BatchJobReturnValue['variants'];
}

export interface BatchCreateParams {
  description: string;
  count?: number;
  dimensions?: Array<{ name: string; values: string[] }>;
  sourcePlayableId?: string;
  projectId?: string;
  imageProvider?: 'jimeng' | 'wanxiang';
}

// ─── Job APIs ──────────────────────────────────────────

/** 获取所有任务或指定 session 的任务列表 */
export function getJobs(sessionId?: string): Promise<{ success: boolean; data: JobItem[] }> {
  const qs = sessionId ? `?sessionId=${sessionId}` : '';
  return get<{ success: boolean; data: JobItem[] }>(`/api/agent/jobs${qs}`);
}

/** 取消等待中的任务 */
export function cancelJob(jobId: string): Promise<{ success: boolean; data: { jobId: string; status: string } }> {
  // DELETE doesn't use JSON body, just call directly
  return authFetch(`/api/agent/job/${jobId}`, { method: 'DELETE' }).then(r => r.json());
}

/** 查询单个任务状态 */
export function getJobStatus(jobId: string): Promise<{ success: boolean; data: Record<string, unknown> }> {
  return get<{ success: boolean; data: Record<string, unknown> }>(`/api/agent/job/${jobId}`);
}

// ─── Batch APIs ────────────────────────────────────────

/** 创建批量生成任务 */
export function createBatch(params: BatchCreateParams): Promise<{ success: boolean; data: { jobId: string; status: string; message: string } }> {
  return post<{ success: boolean; data: { jobId: string; status: string; message: string } }>('/api/agent/batch', params);
}

/** 查询批量任务状态 */
export function getBatchStatus(jobId: string): Promise<{ success: boolean; data: BatchJobStatus }> {
  return get<{ success: boolean; data: BatchJobStatus }>(`/api/agent/batch/${jobId}`);
}

/** 删除批量任务：删除任务记录 + 未发布变体，已发布试玩保留在资产中心 */
export function deleteBatchJob(jobId: string): Promise<{ success: boolean; data: { jobId: string; status: string; removedVariants: number } }> {
  return authFetch(`/api/agent/batch/${jobId}`, { method: 'DELETE' }).then(r => r.json());
}
